import {
  Button,
  H2,
  InputContainer,
  InputLabel,
  InputPassword,
  InputPhoneNumber,
  InputText,
} from "@sellar-io/components";
import { useSellarSession } from "../../../../hooks/useSellarSession";
import { Alert } from "../../../../components/alert/Alert";
import { useUserPasswordReset } from "../../../../hooks/api/v2/user/useUserPasswordReset";
import {
  isSignInConnectionError,
  signInConnectionMessage,
} from "../../../../hooks/signInConnection";
import { updatePassword } from "firebase/auth";
import { ReactNode, useState } from "react";
import TagManager from "react-gtm-module";
import { useForm } from "react-hook-form";
import { useMatch } from "react-router";
import { useSearchParams } from "react-router";
import { Icon, SpinnerLegacy } from "../../../../components";
import * as ROUTES from "../../../../constants/routes";
import { useSellarUser } from "~/hooks";
import { useStorefrontLink } from "~/hooks/api/v2/storefrontLink/useStorefrontLink";
import { useSupplier } from "~/hooks/api/v2/supplier/useSupplier";
import { useUserUpdate } from "~/hooks/api/v2/user/useUserUpdate";
import { useFirebaseUser } from "../../../../hooks/useFirebaseUser.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../hooks/useSignupContext.shared";
import { Mixpanel } from "~/utils/mixpanel";
import { getJwtUrl } from "../signupUrl";
import { TermsAndConditionsConfirmation } from "./BuyerCreationStep/BuyerSignupFormStep";
import { Tooltip } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";

type UserSignupDetailsStepFormType = {
  firstName: string;
  lastName: string;
  password: string;
  smsNumber: string;
};
export const UserSignupDetailsStep = () => {
  const { sellar } = useSellarSession();
  const { user } = useFirebaseUser();
  const { user: sellarUser } = useSellarUser();
  const [searchParams] = useSearchParams();
  const {
    setStep,
    setHasAcceptedTermsAndConditions,
    hasAcceptedTermsAndConditions,
  } = useSignupContext();
  const matchedSupplierParams = useMatch(ROUTES.ORDER_BUYER.pattern);
  const supplierQueryEnabled =
    !!matchedSupplierParams?.params.supplierIdOrUrlId;
  const supplierQuery = useSupplier(
    matchedSupplierParams?.params.supplierIdOrUrlId,
    {
      enabled: supplierQueryEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const storefrontLinkPassword =
    searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
  const businessUserInviteJwt = getJwtUrl(searchParams.get("continue")) ?? null;
  const utmMedium = searchParams.get("utm_medium");

  const storefrontLinkQueryEnabled =
    !!storefrontLinkPassword && !!supplierQuery.data;

  const storefrontLinkQuery = useStorefrontLink(
    storefrontLinkPassword,
    {
      SupplierId: supplierQuery.data?.id ?? 0,
    },
    {
      enabled: storefrontLinkQueryEnabled,
    },
  );
  const formHook = useForm<UserSignupDetailsStepFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      smsNumber: "+44",
    },
  });

  const [loading, setLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const passwordReset = useUserPasswordReset();

  const sendPasswordLink = async () => {
    if (loading || !user?.firebaseUser.email) return;
    setLoading(true);
    setSubmissionError(undefined);
    try {
      await passwordReset.mutateAsync({
        data: { email: user.firebaseUser.email },
      });
      setResetSent(true);
    } catch (error: unknown) {
      setSubmissionError(
        isSignInConnectionError(error)
          ? signInConnectionMessage
          : "We couldn't send the email. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const returnToSignIn = async () => {
    setLoading(true);
    try {
      await sellar.signOut();
    } catch {
      setSubmissionError("We couldn't sign you out. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const userUpdateMutation = useUserUpdate(
    parseInt(sellarUser?.id.toString() ?? "0"),
  );

  const onSubmit = async (data: UserSignupDetailsStepFormType) => {
    if (requiresSignIn || !canContinue || !user || !sellarUser || loading)
      return;
    setLoading(true);
    setSubmissionError(undefined);
    try {
      if (!user) throw new Error("Cannot find user");

      if (!user.firebaseUser.email) throw new Error("Cannot find email");

      const name = `${data.firstName} ${data.lastName}`;
      const smsNumber = data.smsNumber.split(" ").join("");
      Mixpanel.alias(user.firebaseUser.email);
      Mixpanel.people.setOnce({
        $name: name,
        $email: user.firebaseUser.email,
        $firebaseId: user.uid,
        $first_name: data.firstName,
        $last_name: data.lastName,
        $type: "user",
        $emailVerified: true,
        // oxlint-disable-next-line react/purity -- Captured when this deferred submit handler runs.
        $firstLoginDate: new Date(),
      });

      await userUpdateMutation.mutateAsync(
        {
          data: {
            name,
            smsNumber,
          },
          sellarMetadata: {
            storefrontLinkPassword,
            businessUserInviteJwt,
            utmMedium,
          },
        },
        {
          onSuccess: (res) => {
            TagManager.dataLayer({
              dataLayer: {
                event: "account_created",
                name: res.name,
                email: user.firebaseUser.email,
              },
            });
          },
        },
      );

      await updatePassword(user.firebaseUser, data.password);

      // navigate to the marketing preferences step
      setStep(SignupFlowStep.OptInMarketingStep);
    } catch (error: unknown) {
      const code =
        typeof error === "object" && error !== null && "code" in error
          ? error.code
          : undefined;
      if (code === "auth/requires-recent-login") {
        setRequiresSignIn(true);
      } else {
        setSubmissionError(
          isSignInConnectionError(error)
            ? signInConnectionMessage
            : code === "auth/weak-password"
              ? "Please choose a stronger password with at least six characters."
              : "We couldn't finish saving your details. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isStorefrontLinkLoading =
    storefrontLinkQuery.isPending && storefrontLinkQueryEnabled;
  const isSupplierLoading = supplierQuery.isPending && supplierQueryEnabled;

  if (isStorefrontLinkLoading || isSupplierLoading) return <SpinnerLegacy />;

  const canContinue = !businessUserInviteJwt || hasAcceptedTermsAndConditions;
  return (
    <div className={"m-auto flex h-full flex-col justify-center gap-4"}>
      <div className={"flex flex-col gap-2"}>
        <H2 className={"text-center"}>
          Please enter your contact details and create a password
        </H2>
      </div>
      {submissionError && <Alert type="error" message={submissionError} />}
      {requiresSignIn && (
        <div className="flex flex-col gap-2" role="alert">
          <Alert
            type="information"
            message={
              resetSent
                ? "Check your inbox for a link to set your password, then sign in to continue your signup."
                : "Your sign-in is too old to set a password. We'll email you a secure link to set it, then you can sign in to continue."
            }
          />
          <Button
            type="button"
            color="secondary"
            onClick={sendPasswordLink}
            loading={loading}
            disabled={loading}
          >
            {resetSent ? "Resend password link" : "Email me a password link"}
          </Button>
          {resetSent && (
            <Button
              type="button"
              color="secondary"
              onClick={returnToSignIn}
              disabled={loading}
            >
              Return to sign in
            </Button>
          )}
        </div>
      )}
      <form
        className={"flex flex-col gap-2"}
        onSubmit={formHook.handleSubmit(async (data) => {
          await onSubmit(data);
        })}
      >
        <InputLabel>Password</InputLabel>
        <InputContainer>
          <InputPassword
            {...formHook.register("password", { required: true })}
            placeholder={"Password"}
          />
        </InputContainer>
        <div className={"grid gap-2 md:grid-cols-2"}>
          <div className={"flex flex-col gap-2"}>
            <InputLabel>First name</InputLabel>
            <InputContainer>
              <InputText
                {...formHook.register("firstName", { required: true })}
                placeholder={"First name"}
                autoComplete={"given-name"}
              />
            </InputContainer>
          </div>
          <div className={"flex flex-col gap-2"}>
            <InputLabel>Last name</InputLabel>
            <InputContainer>
              <InputText
                {...formHook.register("lastName", { required: true })}
                placeholder={"Last name"}
                autoComplete={"family-name"}
              />
            </InputContainer>
          </div>
        </div>

        <InputLabel className="inline-flex items-center gap-1">
          Mobile phone number{" "}
          <CustomTooltip
            placement={"top"}
            trigger={
              <span className={"cursor-pointer"}>
                <Icon icon={"INFORMATION_OUTLINE"} />
              </span>
            }
            className={"rounded-lg bg-black px-2 py-1 text-sm text-white"}
          >
            Your mobile number is only shared with suppliers when you place an
            order with them
          </CustomTooltip>
        </InputLabel>
        <InputContainer className="overflow-visible">
          <InputPhoneNumber
            {...formHook.register("smsNumber", {
              required: true,
              minLength: 11,
            })}
            onChange={(phone) => {
              formHook.setValue("smsNumber", phone);
            }}
            placeholder="+44 7123456789"
          />
        </InputContainer>

        {!!businessUserInviteJwt && (
          <TermsAndConditionsConfirmation
            onChange={(value) => setHasAcceptedTermsAndConditions(value)}
          />
        )}
        <Button
          disabled={!canContinue || loading || requiresSignIn}
          className={"mt-4 w-full"}
          loading={loading}
          type={"submit"}
        >
          Continue
        </Button>
      </form>
    </div>
  );
};

const CustomTooltip = ({
  placement,
  children,
  trigger,
  className,
}: {
  placement: "top";
  children: ReactNode;
  trigger: ReactNode;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip placement={placement} open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((open) => !open)}
      >
        {trigger}
      </TooltipTrigger>
      <TooltipContent className={twMerge(className)}>{children}</TooltipContent>
    </Tooltip>
  );
};
